import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button.tsx';
import WrapperImage from '@components/image/WrapperImage.tsx';
import SvgClose from '@components/icons/Close.tsx';
import { IconWrapper } from '@components/icons/IconWrapper.tsx';
import { clientLovApi, clientProductControllerApi } from '@api/api-config.ts';
import { ClientLoVSupplier } from '@api/generated/search-service';
import { formatUnit } from '@utils/formats.ts';

interface AddSupplierSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
  productName: string;
  productImageUrl?: string;
  organizationId: number;
  portalId: number;
  auctionAmount?: number;
  auctionUnit?: string;
  onSuccess?: () => void;
}

const AddSupplierSidebar: React.FC<AddSupplierSidebarProps> = ({ isOpen, onClose, productId, productName, productImageUrl, organizationId, portalId, auctionAmount, auctionUnit, onSuccess }) => {
  const { t } = useTranslation('client');
  const [step, setStep] = useState<'select' | 'form'>('select');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<ClientLoVSupplier | null>(null);
  const [suppliers, setSuppliers] = useState<ClientLoVSupplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  // Form fields
  const [catalogNumber, setCatalogNumber] = useState('');
  const [catalogName, setCatalogName] = useState('');
  const [price, setPrice] = useState('');
  const [validTo, setValidTo] = useState('');
  const [auctionPrice, setAuctionPrice] = useState('');
  const [minimumQty, setMinimumQty] = useState('1');
  const [multiply, setMultiply] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    setLoadingSuppliers(true);
    clientLovApi
      .getClientSuppliers({ organizationId, portalId })
      .then(response => {
        setSuppliers(response.data ?? []);
      })
      .catch(() => {
        setSuppliers([]);
      })
      .finally(() => setLoadingSuppliers(false));
  }, [isOpen]);

  const filteredSuppliers = suppliers.filter(s => s.supplierName.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleSelectSupplier = () => {
    if (selectedSupplier) {
      setStep('form');
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!catalogNumber.trim()) errors.catalogNumber = t('addSupplier.validation.catalogNumberRequired');
    if (!catalogName.trim()) errors.catalogName = t('addSupplier.validation.catalogNameRequired');
    const normalizedPrice = price.trim().replace(',', '.');
    if (!normalizedPrice || isNaN(Number(normalizedPrice)) || Number(normalizedPrice) < 0) errors.price = t('addSupplier.validation.priceRequired');
    if (!validTo) errors.validTo = t('addSupplier.validation.validToRequired');
    const normalizedMinimumQty = minimumQty.trim().replace(',', '.');
    if (!normalizedMinimumQty || isNaN(Number(normalizedMinimumQty)) || Number(normalizedMinimumQty) <= 0) errors.minimumQty = t('addSupplier.validation.minimumQtyRequired');
    if (auctionAmount && auctionUnit) {
      const normalizedAuctionPrice = auctionPrice.trim().replace(',', '.');
      if (!normalizedAuctionPrice || isNaN(Number(normalizedAuctionPrice)) || Number(normalizedAuctionPrice) < 0) errors.auctionPrice = t('addSupplier.validation.auctionPriceRequired');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const [_, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      await clientProductControllerApi.addManualSupplier({
        organizationId,
        portalId,
        productId,
        addManualSupplierRequest: {
          supplierId: selectedSupplier!.supplierId,
          catalogCode: catalogNumber,
          catalogName,
          priceWithoutVat: Number(price.replace(',', '.')),
          auctionPrice: auctionPrice ? Number(auctionPrice.replace(',', '.')) : undefined,
          validTo,
          minimumQty: Number(minimumQty.replace(',', '.')),
          multiply,
        },
      });
      onSuccess?.();
      handleClose();
    } catch (e) {
      console.error('Chyba při přidávání dodavatele:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('select');
    setSearchQuery('');
    setSelectedSupplier(null);
    setCatalogNumber('');
    setCatalogName('');
    setPrice('');
    setAuctionPrice('');
    setValidTo('');
    setMinimumQty('1');
    setMultiply(false);
    setFormErrors({});
    onClose();
  };

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={handleClose} />}

      <aside
        className={[
          'fixed top-0 right-0 w-full h-[100dvh] md:w-[460px] bg-white transform transition-transform duration-300 ease-in-out shadow-lg z-50',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
      >
        {isOpen && (
          <div className="flex flex-col h-full p-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">{t('addSupplier.title')}</h2>
              <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded">
                <IconWrapper className="w-5 h-5">
                  <SvgClose />
                </IconWrapper>
              </button>
            </div>

            {/* Product info */}
            <div className="mb-6">
              <div className="basic-body-s text-light mb-2">{t('addSupplier.forProduct')}</div>
              <div className="flex flex-row gap-3 items-center">
                <div className="w-12 h-12 shrink-0 overflow-hidden rounded">
                  <WrapperImage src={productImageUrl ?? ''} />
                </div>
                <div className="basic-body-m-bold">{productName}</div>
              </div>
            </div>

            <hr className="mb-6" />

            {step === 'select' && (
              <div className="flex flex-col flex-1 overflow-hidden">
                <div className="basic-body-m mb-3">{t('addSupplier.selectFromList')}</div>

                {/* Search */}
                <div className="relative mb-4">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder={t('addSupplier.searchPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-surface-dark-primary pr-10"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                  </div>
                </div>

                {/* Supplier list */}
                <div className="flex-1 overflow-y-auto flex flex-col gap-3">
                  {loadingSuppliers && (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-surface-dark-primary" />
                    </div>
                  )}
                  {!loadingSuppliers &&
                    filteredSuppliers.map(supplier => (
                      <div
                        key={supplier.supplierId}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          selectedSupplier?.supplierId === supplier.supplierId ? 'border-surface-dark-primary bg-surface-lighter-primary' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedSupplier(supplier)}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                              selectedSupplier?.supplierId === supplier.supplierId ? 'border-surface-dark-primary' : 'border-gray-300'
                            }`}
                          >
                            {selectedSupplier?.supplierId === supplier.supplierId && <div className="w-2.5 h-2.5 rounded-full bg-surface-dark-primary" />}
                          </div>
                          <div>
                            <div className="basic-body-m-bold">{supplier.supplierName}</div>
                            <div className="basic-body-xs text-light">
                              {t('addSupplier.supplierId')}: {supplier.supplierId}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  {!loadingSuppliers && filteredSuppliers.length === 0 && <div className="text-center py-8 text-light basic-body-s">{t('addSupplier.noSuppliers')}</div>}
                </div>

                {/* Select button */}
                <div className="pt-4 flex justify-center">
                  <Button label={t('addSupplier.selectSupplier')} type="primary" variant="fill" size="md" onClick={handleSelectSupplier} disabled={!selectedSupplier} />
                </div>
              </div>
            )}

            {step === 'form' && selectedSupplier && (
              <div className="flex flex-col flex-1 overflow-hidden">
                {/* Selected supplier card */}
                <div className="p-4 border-2 border-surface-dark-primary rounded-lg mb-6">
                  <div className="basic-body-m-bold">{selectedSupplier.supplierName}</div>
                  <div className="basic-body-xs text-light">
                    {t('addSupplier.supplierId')}: {selectedSupplier.supplierId}
                  </div>
                </div>

                {/* Form */}
                <div className="flex flex-col gap-4 flex-1">
                  <div className="flex flex-col gap-1">
                    <label className="basic-body-s text-light">{t('addSupplier.catalogNumber')}*</label>
                    <input
                      type="text"
                      value={catalogNumber}
                      onChange={e => setCatalogNumber(e.target.value)}
                      placeholder={t('addSupplier.catalogNumberPlaceholder')}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-surface-dark-primary ${formErrors.catalogNumber ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    {formErrors.catalogNumber && <span className="text-red-500 text-sm">{formErrors.catalogNumber}</span>}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="basic-body-s text-light">{t('addSupplier.catalogName')}*</label>
                    <input
                      type="text"
                      value={catalogName}
                      onChange={e => setCatalogName(e.target.value)}
                      placeholder={t('addSupplier.catalogNamePlaceholder')}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-surface-dark-primary ${formErrors.catalogName ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    {formErrors.catalogName && <span className="text-red-500 text-sm">{formErrors.catalogName}</span>}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="basic-body-s text-light">{t('addSupplier.priceWithoutVat')}*</label>
                    <input
                      type="text"
                      value={price}
                      onChange={e => setPrice(e.target.value)}
                      placeholder="0.00"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-surface-dark-primary ${formErrors.price ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    {formErrors.price && <span className="text-red-500 text-sm">{formErrors.price}</span>}
                  </div>

                  {auctionAmount && auctionUnit && (
                    <div className="flex flex-col gap-1">
                      <label className="basic-body-s text-light">Cena za {auctionAmount} {formatUnit(auctionUnit as any)} (Kč)*</label>
                      <input
                        type="text"
                        value={auctionPrice}
                        onChange={e => setAuctionPrice(e.target.value)}
                        placeholder="0.00"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-surface-dark-primary ${formErrors.auctionPrice ? 'border-red-400' : 'border-gray-300'}`}
                      />
                      {formErrors.auctionPrice && <span className="text-red-500 text-sm">{formErrors.auctionPrice}</span>}
                    </div>
                  )}

                  <div className="flex flex-col gap-1">
                    <label className="basic-body-s text-light">{t('addSupplier.minimumQty')}*</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={minimumQty}
                      onChange={e => setMinimumQty(e.target.value)}
                      placeholder="1"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-surface-dark-primary ${formErrors.minimumQty ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    <span className="basic-body-xs text-light">{t('addSupplier.minimumQtyHint')}</span>
                    {formErrors.minimumQty && <span className="text-red-500 text-sm">{formErrors.minimumQty}</span>}
                  </div>

                  <label className="flex items-start gap-2 basic-body-s text-main">
                    <input type="checkbox" className="mt-1" checked={multiply} onChange={e => setMultiply(e.target.checked)} />
                    <span>{t('addSupplier.multiply')}</span>
                  </label>

                  <div className="flex flex-col gap-1">
                    <label className="basic-body-s text-light">{t('addSupplier.validTo')}*</label>
                    <input
                      type="date"
                      value={validTo}
                      onChange={e => setValidTo(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-surface-dark-primary ${formErrors.validTo ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    {formErrors.validTo && <span className="text-red-500 text-sm">{formErrors.validTo}</span>}
                  </div>
                </div>

                {/* Submit button */}
                <div className="pt-4 flex justify-center">
                  <Button label={t('addSupplier.submit')} type="primary" variant="fill" size="md" onClick={handleSubmit} />
                </div>
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default AddSupplierSidebar;
